import React, { useEffect, useRef } from "react";
import "./AdSenseSlot.css";

const AD_CLIENT = "ca-pub-7640498129575554";
const AD_SLOT = process.env.REACT_APP_ADSENSE_SLOT || "9782744681";

const ADS_ENABLED =
  process.env.REACT_APP_ENABLE_ADSENSE !== "false";

interface AdSenseSlotProps {
  className?: string;
}

/**
 * Loads one AdSense display unit. Waits for the script from index.html,
 * and guards against duplicate adsbygoogle.push() calls.
 */
function AdSenseSlot({ className = "" }: AdSenseSlotProps) {
  const insRef = useRef<HTMLModElement>(null);
  const initStartedRef = useRef(false);

  useEffect(() => {
    if (!ADS_ENABLED) return;

    const el = insRef.current;
    if (!el || initStartedRef.current) return;
    if (el.getAttribute("data-adsbygoogle-status")) return;

    const requestAd = () => {
      if (!insRef.current || initStartedRef.current) return;
      if (insRef.current.getAttribute("data-adsbygoogle-status")) return;

      initStartedRef.current = true;
      try {
        const adsbygoogle = (window.adsbygoogle ??= []);
        adsbygoogle.push({});
      } catch (err) {
        initStartedRef.current = false;
        if (process.env.NODE_ENV === "development") {
          console.warn("[AdSense]", err);
        }
      }
    };

    if (window.adsbygoogle) {
      requestAd();
      return;
    }

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (window.adsbygoogle) {
        window.clearInterval(timer);
        requestAd();
      } else if (attempts > 50) {
        window.clearInterval(timer);
      }
    }, 100);

    return () => window.clearInterval(timer);
  }, []);

  if (!ADS_ENABLED) {
    return null;
  }

  const isDev = process.env.NODE_ENV === "development";

  return (
    <div
      className={`AdSenseSlot-wrapper${className ? ` ${className}` : ""}`}
      role="complementary"
      aria-label="Advertisement"
    >
      <ins
        ref={insRef}
        className="adsbygoogle AdSenseSlot"
        style={{ display: "block" }}
        data-ad-client={AD_CLIENT}
        data-ad-slot={AD_SLOT}
        data-ad-format="auto"
        data-full-width-responsive="true"
        {...(isDev ? { "data-adtest": "on" } : {})}
      />
    </div>
  );
}

export default AdSenseSlot;
